import { InputAdornment } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { NumberField } from '@superdispatch/ui';
import { useField } from 'formik';
import { useEffect, useState } from 'react';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { kmToMile, mileToKm } from 'shared/modules/loadboard/LoadboardUtils';
import { VENUE_RADIUS_MILE_OPTIONS } from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { ensurePositiveNumber } from 'shared/utils/YupUtils';

const options = VENUE_RADIUS_MILE_OPTIONS.map((value) => ({
  value,
  text: String(value),
}));

interface Props {
  name: string;
}

export function AdvancedSearchRadiusField({ name }: Props) {
  const [{ value, onBlur }, { error }, helpers] = useField<number | null>({
    name,
    validate: (input) => {
      if (input == null) return undefined;

      if (input < mileToKm(5)) return '5 mi min.';
      if (input > mileToKm(500)) return '500 mi max.';
      return undefined;
    },
  });
  const [inputValue, setInputValue] = useState(50);

  useEffect(() => {
    setInputValue(value == null ? 50 : kmToMile(value, 0));
  }, [value]);

  function handleChange(changedValue: number) {
    if (changedValue === 50) {
      return helpers.setValue(null, true);
    }
    return helpers.setValue(mileToKm(changedValue, 6), true);
  }

  return (
    <Autocomplete
      options={options}
      freeSolo={true}
      autoHighlight={false}
      selectOnFocus={false}
      disableClearable={true}
      value={{ value: inputValue, text: String(inputValue) }}
      onInputChange={(_event, changedValue) => {
        setInputValue(ensurePositiveNumber(changedValue));
      }}
      onChange={(_event, changedValue) => {
        if (typeof changedValue !== 'string') {
          void handleChange(changedValue.value);
        }
      }}
      onBlur={(event) => {
        void handleChange(inputValue).then(() => {
          onBlur(event);
        });
      }}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.text
      }
      getOptionSelected={(option, selectedValue) =>
        option.value === selectedValue.value
      }
      filterOptions={(items) => items}
      renderInput={(params) => (
        <NumberField
          {...params}
          isText={true}
          label={isWebView ? 'Radius' : <span>&nbsp;</span>}
          aria-label={name}
          fullWidth={true}
          error={!!error}
          helperText={error}
          inputProps={{ ...params.inputProps, style: { width: '20px' } }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end" disableTypography={true}>
                mi
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
}
